import type { IConfigCatClient } from 'configcat-react';

/**
 * ConfigCat feature flag helpers. The client itself lives in React context
 * (see components/ConfigCatClientBridge), so this module exposes flag
 * checks that work both from components and from plain module-level code
 * (e.g. utills/telemetry.ts) via a module-level client reference kept in
 * sync by the bridge.
 */

// Every new flag: add a name here and a matching key in the ConfigCat dashboard.
export type FlagName = 'enableTelemetry';

// configcat-react only creates its client inside the React tree (via
// ConfigCatProvider), but flag checks here happen from plain module-level
// code (see utills/telemetry.ts), so ConfigCatClientBridge injects the
// client once the Provider has mounted.
let client: IConfigCatClient | null = null;
const listeners: Array<() => void> = [];

// Last resolved value per flag, so synchronous call sites (e.g. header
// builders in routers/index.tsx, PDFReader) can read the current status
// without awaiting isFeatureEnabled every time.
const cachedValues = new Map<FlagName, boolean>();

/**
 * Called by ConfigCatClientBridge whenever the ConfigCat client mounts,
 * unmounts, or its config changes. Registers the client (or clears it) and
 * notifies subscribers, since the client's availability can itself change
 * what a flag resolves to.
 * @param newClient the ConfigCat client instance, or null when unmounting
 */
export const setConfigCatClient = (newClient: IConfigCatClient | null): void => {
  client = newClient;
  if (client) {
    client.on('configChanged', () => listeners.forEach((listener) => listener()));
  }
  // The client becoming available (or unavailable) can itself change what a
  // flag resolves to, so treat it the same as a config change.
  listeners.forEach((listener) => listener());
};

/**
 * Resolves a feature flag's current value, caching the result for
 * getCachedFeatureFlag's synchronous reads. Falls back to defaultValue when
 * the ConfigCat client hasn't been published yet (e.g. before
 * ConfigCatClientBridge mounts).
 * @param flag the flag to check
 * @param defaultValue value to use when the client isn't available yet
 */
export const isFeatureEnabled = (
  flag: FlagName,
  defaultValue: boolean
): Promise<boolean> => {
  if (!client) {
    cachedValues.set(flag, defaultValue);
    return Promise.resolve(defaultValue);
  }
  return client.getValueAsync(flag, defaultValue).then((value) => {
    cachedValues.set(flag, value);
    return value;
  });
};

/**
 * Synchronous read of the last value isFeatureEnabled resolved for this
 * flag. Falls back to defaultValue if the flag hasn't been resolved yet.
 * @param flag the flag to read
 * @param defaultValue value to use when nothing has been cached yet
 */
export const getCachedFeatureFlag = (
  flag: FlagName,
  defaultValue: boolean
): boolean => (cachedValues.has(flag) ? (cachedValues.get(flag) as boolean) : defaultValue);

/**
 * Registers a listener invoked whenever a flag's config changes or the
 * ConfigCat client becomes available/unavailable.
 * @param listener called with no arguments on every relevant change
 */
export const onFlagsChanged = (listener: () => void): void => {
  listeners.push(listener);
};
